import React, { useEffect, useState, useCallback } from "react";
import { useLiveSync } from "@/lib/useLiveSync";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { ListChecks, Plus, Trash2, Check } from "lucide-react";
import { toast } from "sonner";

export default function Chores() {
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [tab, setTab] = useState("open");
  const [open, setOpen] = useState(false);
  const blank = { title: "", assignee_id: "", recurrence: "weekly", due_date: "", done: false, notes: "" };
  const [form, setForm] = useState(blank);

  const load = useCallback(async () => {
    const [c, hh] = await Promise.all([api.get("/chores"), api.get("/household/me")]);
    setItems(c.data); setMembers(hh.data.members || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  useLiveSync(load, 10000);

  const filtered = items.filter((i) => (tab === "open" ? !i.done : i.done));

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    try {
      const payload = { ...form, assignee_id: form.assignee_id || null };
      await api.post("/chores", payload);
      setOpen(false); setForm(blank); toast.success("Saved"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggle = async (id) => { await api.post(`/chores/${id}/toggle`); load(); };
  const remove = async (id) => { await api.delete(`/chores/${id}`); load(); };
  const memberName = (id) => members.find((m) => m.id === id)?.name || "Anyone";

  return (
    <div>
      <PageHeader
        eyebrow="Shared work"
        title="Chores"
        subtitle="Who's doing what, when. Keep the load fair."
        action={
          <Button data-testid="chore-add-button" onClick={() => setOpen(true)}
            className="rounded-full bg-hearth-terracotta hover:bg-hearth-terracottaDark text-white px-6 h-11">
            <Plus size={16} className="mr-1.5" /> Add chore
          </Button>
        }
      />

      <div className="bg-hearth-paper border border-hearth-line rounded-full p-1 inline-flex mb-6">
        {[["open", "To do"], ["done", "Done"]].map(([k, l]) => (
          <button key={k} data-testid={`chore-tab-${k}`} onClick={() => setTab(k)}
            className={`px-5 py-2 text-sm rounded-full transition ${tab === k ? "bg-hearth-terracotta text-white" : "text-hearth-mute hover:text-hearth-ink"}`}>{l}</button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={ListChecks} title={tab === "open" ? "Nothing on the list." : "No completed chores yet."} message={tab === "open" ? "Add chores like 'water plants' or 'take out bins' and assign them to anyone in the house." : ""} />
      ) : (
        <div className="warm-card divide-y divide-hearth-line/70">
          {filtered.map((c) => (
            <div key={c.id} data-testid={`chore-row-${c.id}`} className="flex items-center gap-3 p-5">
              <button data-testid={`chore-toggle-${c.id}`} onClick={() => toggle(c.id)}
                      className="w-8 h-8 rounded-full border-2 border-hearth-line hover:border-hearth-terracotta grid place-items-center shrink-0 transition">
                {c.done && <Check size={14} className="text-hearth-sage" />}
              </button>
              <div className="flex-1 min-w-0">
                <div className={`text-lg ${c.done ? "line-through text-hearth-mute" : "text-hearth-ink"}`}>{c.title}</div>
                <div className="caption text-hearth-mute mt-0.5">
                  {memberName(c.assignee_id)} · {c.recurrence}{c.due_date ? ` · ${c.due_date}` : ""}
                </div>
              </div>
              <button data-testid={`chore-delete-${c.id}`} onClick={() => remove(c.id)} className="p-2 rounded-full text-hearth-mute hover:text-hearth-coral hover:bg-hearth-coral/10">
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl border-hearth-line bg-hearth-paper max-w-md">
          <DialogHeader><DialogTitle className="font-display text-3xl">New chore</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="caption text-hearth-mute">Title</Label>
              <Input data-testid="chore-form-title" className="input-warm mt-1.5 h-11" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Water the plants" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="caption text-hearth-mute">Assigned to</Label>
                <Select value={form.assignee_id || "any"} onValueChange={(v) => setForm({ ...form, assignee_id: v === "any" ? "" : v })}>
                  <SelectTrigger className="input-warm mt-1.5 h-11 rounded-2xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Anyone</SelectItem>
                    {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="caption text-hearth-mute">Recurrence</Label>
                <Select value={form.recurrence} onValueChange={(v) => setForm({ ...form, recurrence: v })}>
                  <SelectTrigger className="input-warm mt-1.5 h-11 rounded-2xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="once">Once</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="caption text-hearth-mute">Due date</Label>
              <Input type="date" className="input-warm mt-1.5 h-11" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="chore-form-save" onClick={save} className="rounded-full bg-hearth-terracotta hover:bg-hearth-terracottaDark text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
